import React, {useEffect, useMemo, useRef, useState} from 'react';
import InputForm from "./input-form.jsx";
import {DayPicker} from "react-day-picker";
import {ptBR} from "react-day-picker/locale";
import {FaCalendar, FaChevronDown, FaChevronUp, FaClock} from "react-icons/fa";
import {format, isValid, parse, set} from "date-fns";
import "../../css/input-date-time.css"
import {IMask} from "react-imask";
import "react-day-picker/dist/style.css";

const HOUR_OPTIONS = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23];
const MINUTE_OPTIONS = Array.from({length: 12}, (_, index) => index * 5);

const TimeColumn = ({ value, max, onChangeValue, onOpenSelection, selectionLabel }) => {
    const formattedValue = String(value).padStart(2, "0");
    const hasSelection = Boolean(onOpenSelection);

    const handleKeyDown = (event) => {
        if (!hasSelection || !["Enter", " "].includes(event.key)) return;

        event.preventDefault();
        onOpenSelection();
    };

    return (
        <div className="flex flex-col items-center w-15">
            <button
                type="button"
                className="p-1 text-gray-400 hover:text-blue-600 focus:outline-none focus:text-blue-600 transition-colors cursor-pointer"
                onClick={() => onChangeValue(1, max)}
            >
                <FaChevronUp size={14} />
            </button>

            <input
                type="text"
                className={["py-2.5 border-none w-15 text-center outline-none bg-white! hover:bg-[#e9ecef]!", hasSelection ? "cursor-pointer" : ""].join(" ")}
                value={formattedValue}
                onClick={onOpenSelection}
                onKeyDown={handleKeyDown}
                readOnly={hasSelection}
                aria-label={selectionLabel}
            />

            <button
                type="button"
                className="p-1 text-gray-400 hover:text-blue-600 focus:outline-none focus:text-blue-600 transition-colors cursor-pointer"
                onClick={() => onChangeValue(-1, max)}
            >
                <FaChevronDown size={14} />
            </button>
        </div>
    );
};

const isDateValue = (value) => value instanceof Date && isValid(value);

const DATE_PATTERNS = [
    { format: "dd/MM/yyyy HH:mm:ss", regex: /^\d{2}\/\d{2}\/\d{4} \d{2}:\d{2}:\d{2}$/ },
    { format: "dd/MM/yyyy HH:mm", regex: /^\d{2}\/\d{2}\/\d{4} \d{2}:\d{2}$/ },
    { format: "dd/MM/yyyy", regex: /^\d{2}\/\d{2}\/\d{4}$/ },
    { format: "yyyy-MM-dd HH:mm:ss", regex: /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/ },
    { format: "yyyy-MM-dd HH:mm", regex: /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}$/ },
    { format: "yyyy-MM-dd", regex: /^\d{4}-\d{2}-\d{2}$/ },
    { format: "yyyy-MM-dd'T'HH:mm:ss.SSSXXX", regex: /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{1,3})?(Z|[+-]\d{2}:\d{2})?$/ },
    { format: "HH:mm:ss", regex: /^(?:[01]\d|2[0-3]):[0-5]\d:[0-5]\d$/ },
    { format: "HH:mm", regex: /^(?:[01]\d|2[0-3]):[0-5]\d$/ },
];

const getParsedDate = (value, dateFormat) => {
    if (isDateValue(value)) return value;
    if (!value || typeof value !== "string") return undefined;

    const trimmedValue = value.trim();

    if (!trimmedValue) return undefined;

    for (const {format: candidateFormat, regex} of DATE_PATTERNS) {
        if (!regex.test(trimmedValue)) continue;

        const parsedDate = parse(trimmedValue, candidateFormat, new Date());

        if (isValid(parsedDate)) {
            return parsedDate;
        }
    }

    return undefined;
};

const getDateFormat = (dateFormat) => {
    const enums = {
        LTS: 'HH:mm:ss',
        LT: 'HH:mm',
        L: 'dd/MM/yyyy',
        LL: 'dd/MM/yyyy HH:mm',
        LLL: 'dd/MM/yyyy HH:mm:ss',
    }

    return enums[dateFormat] ?? "dd/MM/yyyy HH:mm"
}

const padPart = (value, length) => String(value ?? "").padStart(length, "0");

const normalizePartialInput = (value, dateFormat_) => {
    if (typeof value !== "string") return value;

    const trimmed = value.trim();
    if (!trimmed) return value;

    const now = new Date();
    const curDD = padPart(now.getDate(), 2);
    const curMM = padPart(now.getMonth() + 1, 2);
    const curYYYY = padPart(now.getFullYear(), 4);

    const part = (arr, idx) => (arr[idx] ?? "").trim();

    const fillDate = (parts) => {
        const dd = part(parts, 0) || curDD;
        const mm = part(parts, 1) || curMM;
        const yyyy = part(parts, 2) || curYYYY;
        return `${padPart(dd, 2)}/${padPart(mm, 2)}/${padPart(yyyy, 4)}`;
    };

    const fillTime = (parts, withSeconds) => {
        const hh = part(parts, 0) || "00";
        const mi = part(parts, 1) || "00";
        const ss = part(parts, 2) || "00";
        const base = `${padPart(hh, 2)}:${padPart(mi, 2)}`;
        return withSeconds ? `${base}:${padPart(ss, 2)}` : base;
    };

    if (dateFormat_ === "LTS") return fillTime(trimmed.split(":"), true);
    if (dateFormat_ === "LT") return fillTime(trimmed.split(":"), false);
    if (dateFormat_ === "L") return fillDate(trimmed.split("/"));

    if (dateFormat_ === "LL" || dateFormat_ === "LLL") {
        const [datePart = "", timePart = ""] = trimmed.split(" ");
        return `${fillDate(datePart.split("/"))} ${fillTime(timePart.split(":"), dateFormat_ === "LLL")}`;
    }

    return value;
};

const InputDatetime = ({id, label, onChange, selected, dateFormat: dateFormat_ = "LL", classNameWrapper, returnDateObj = true, disabled, inputClass = "", labelClass = "", restrictions, customIcon, hasIcon = true, addDateOnClick = true, autocomplete = 'off', required = false, invalid = false, reference}) => {
    const [month, setMonth] = useState(new Date());
    const [dayPickerValue, setDayPickerValue] = useState(undefined);
    const [time, setTime] = useState({ h: 0, m: 0, s: 0 });
    const [inputValue, setInputValue] = useState("");

    const [calendar, setCalendar] = useState(true);
    const [timeSelection, setTimeSelection] = useState(null);
    const [isOpen, setIsOpen] = useState(false);
    const [openUpward, setOpenUpward] = useState(false);
    const [alignRight, setAlignRight] = useState(false);

    const containerRef = useRef(null);
    const inputRef = useRef(null);
    const isOpenRef = useRef(false);

    const isTimeOnly = ["LTS", "LT"].includes(dateFormat_);
    const dateFormat = useMemo(() => getDateFormat(dateFormat_), [dateFormat_]);
    const hasValidValue = Boolean(getParsedDate(inputValue));

    const first = useRef(true);

    const maskConfig = useMemo(() => {
        const timeBlocks = {
            HH: {
                mask: IMask.MaskedRange,
                from: 0,
                to: 23,
                maxLength: 2,
                autofix: false
            },
            mm: {
                mask: IMask.MaskedRange,
                from: 0,
                to: 59,
                maxLength: 2,
                autofix: false
            },
            ss: {
                mask: IMask.MaskedRange,
                from: 0,
                to: 59,
                maxLength: 2,
                autofix: false
            },
            DD: { mask: IMask.MaskedRange, from: 1, to: 31 },
            MM: { mask: IMask.MaskedRange, from: 1, to: 12 },
            YYYY: { mask: IMask.MaskedRange, from: 1900, to: 9999 }
        };

        if (dateFormat_ === "LTS") {
            return {
                mask: "HH:mm:ss",
                blocks: timeBlocks,
                autofix: false
            };
        }

        if (dateFormat_ === "LT") {
            return {
                mask: "HH:mm",
                blocks: timeBlocks,
                autofix: false
            };
        }

        if (dateFormat_ === "LLL") {
            return {
                mask: "DD/MM/YYYY HH:mm:ss",
                blocks: timeBlocks,
                autofix: false
            };
        }

        if (dateFormat_ === "LL") {
            return {
                mask: "DD/MM/YYYY HH:mm",
                blocks: timeBlocks,
                autofix: false
            };
        }

        return {
            mask: "DD/MM/YYYY",
            blocks: timeBlocks,
            autofix: false
        };
    }, [dateFormat_]);

    const syncDateState = (date, input = true) => {
        setDayPickerValue(date);
        setMonth(date);
        setTime({
            h: date.getHours(),
            m: date.getMinutes(),
            s: date.getSeconds(),
        });

        if (input) setInputValue(format(date, dateFormat));
    };

    const clearDateState = (nextInputValue = "") => {
        setDayPickerValue(undefined);
        setInputValue(nextInputValue);
    };

    const syncFromSelectedValue = (value, triggerChange = false) => {
        if (isOpenRef.current || !value) return;

        let parsedDate = getParsedDate(value);
        let wasNormalized = false;

        if (!parsedDate && triggerChange && typeof value === "string") {
            const candidate = normalizePartialInput(value, dateFormat_);
            if (candidate !== value) {
                const parsedCandidate = getParsedDate(candidate);
                if (parsedCandidate) {
                    parsedDate = parsedCandidate;
                    wasNormalized = true;
                }
            }
        }

        if (parsedDate) {
            syncDateState(parsedDate);
            if (wasNormalized) emitChange(parsedDate);
            return parsedDate;
        }

        clearDateState(typeof value === "string" ? value : "");
        if (!value) {
            setTime({ h: 0, m: 0, s: 0 });
        }

        if (triggerChange) emitChange(null);

        return undefined;
    }

    useEffect(() => {
        function handleClickOutside(event) {
            if (containerRef.current && !containerRef.current.contains(event.target)) closePopover()
        }

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    useEffect(() => {
        if (!selected) {
            setDayPickerValue(undefined);
            setInputValue("");
            setTime({ h: 0, m: 0, s: 0 });
            return;
        }

        const parsedDate = syncFromSelectedValue(selected);
        if (parsedDate && first.current) {
            emitChange(parsedDate);
            first.current = false
        }
    }, [selected]);

    useEffect(() => {
        if (!hasValidValue || !inputRef.current) return;

        inputRef.current.dispatchEvent(new Event("input", {bubbles: true}));
    }, [hasValidValue, inputValue]);

    const emitChange = (nextValue) => {
        if (!nextValue) {
            onChange(null);
            return;
        }

        onChange(returnDateObj ? nextValue : format(nextValue, dateFormat));
    };

    const handleInputChange = (input) => {
        const nextValue = input?.target?.value ?? "";
        setInputValue(nextValue);

        if (!nextValue) {
            setDayPickerValue(undefined);
            setTime({ h: 0, m: 0, s: 0 });
            emitChange(null);
            return;
        }

        const parsedDate = getParsedDate(nextValue);

        if (parsedDate) {
            syncDateState(parsedDate, false);
            emitChange(parsedDate);
            return;
        }

        clearDateState(nextValue);
    };

    const closePopover = () => {
        isOpenRef.current = false;
        setIsOpen(false);
        setCalendar(true);
        setTimeSelection(null);
    };

    const handleDayPickerSelect = (date) => {
        if (!date) {
            clearDateState("");
            setTime({ h: 0, m: 0, s: 0 });
            emitChange(null);
            return;
        }

        const newDate = new Date(
            date.getFullYear(),
            date.getMonth(),
            date.getDate(),
            time.h,
            time.m,
            time.s
        );

        syncDateState(newDate);

        emitChange(newDate);

        if (!dateFormat.includes("HH:mm")) {
            closePopover();
        }
    };

    const handleTimeChange = (field, amount, max) => {
        let newVal = time[field] + amount;

        if (newVal < 0) newVal = max;
        if (newVal > max) newVal = 0;

        applyTimeUpdate({ ...time, [field]: newVal });
    };

    const applyTimeUpdate = (newTime) => {
        setTime(newTime);

        const baseDate = dayPickerValue ?? getParsedDate(selected) ?? new Date();

        const newDate = set(baseDate, {
            hours: newTime.h,
            minutes: newTime.m,
            seconds: newTime.s
        });

        syncDateState(newDate);
        emitChange(newDate);
    }

    const openTimeSelection = (field) => {
        setTimeSelection({
            field,
            label: field === "h" ? "Selecionar hora" : field === "m" ? "Selecionar minuto" : "Selecionar segundo",
            options: field === "h" ? HOUR_OPTIONS : MINUTE_OPTIONS,
        });
    };

    const handleTimeSelection = (value) => {
        applyTimeUpdate({...time, [timeSelection.field]: value});
        setTimeSelection(null);
    };

    const getInitialDateFromRestrictions = () => {
        if (!restrictions) return new Date();

        const list = Array.isArray(restrictions) ? restrictions : [restrictions];

        for (const r of list) {
            const beforeDate = getParsedDate(r.before);
            if (beforeDate) return beforeDate;

            const afterDate = getParsedDate(r.after);
            if (afterDate) return afterDate;
        }

        return new Date();
    };

    const handleOnClick = () => {
        if (isOpenRef.current) return;

        if (addDateOnClick) {
            const existing = getParsedDate(selected);
            const parsedDate = existing ?? getInitialDateFromRestrictions();

            syncDateState(parsedDate);

            if (!existing) emitChange(parsedDate);
        }

        const rect = containerRef.current?.getBoundingClientRect();
        if (rect) {
            const popoverHeight = isTimeOnly ? 230 : 360;
            const spaceBelow = window.innerHeight - rect.bottom;
            setOpenUpward(spaceBelow < popoverHeight && rect.top > spaceBelow);

            const popoverWidth = 300;
            const spaceRight = window.innerWidth - rect.left;
            setAlignRight(spaceRight < popoverWidth && rect.right > spaceRight);
        }

        isOpenRef.current = true;
        setIsOpen(true);
    };

    const handleKeyDown = (event) => {
        if (event.key === "Tab") {
            closePopover();
            inputRef.current?.blur();
        }

        if (event.key === "Escape") {
            closePopover();
            inputRef.current?.blur();
        }

        if (event.key === "Enter") {
            closePopover();
            inputRef.current?.blur();
        }
    };

    return (
        <div
            className={["sentry-date-timepicker relative", classNameWrapper].join(" ")}
            ref={containerRef}
        >
            <InputForm
                id={id}
                label={label}
                value={inputValue}
                onChange={handleInputChange}
                onClick={handleOnClick}
                onFocus={handleOnClick}
                onBlur={() => syncFromSelectedValue(inputValue, true)}
                onKeyDown={handleKeyDown}
                inputRef={inputRef}
                disabled={disabled}
                inputClass={inputClass}
                labelClass={labelClass}
                autoComplete={autocomplete}
                required={required}
                invalid={invalid && !hasValidValue}
                reference={reference}
                {...maskConfig}
            />

            {
                hasIcon && (
                    customIcon ? customIcon : isTimeOnly ? <FaClock className={"absolute top-2.25 right-2.5"} /> : <FaCalendar className={"absolute top-2.25 right-2.5"} />
                )
            }

            {
                isOpen &&
                <div className={["absolute z-[101] bg-white border border-gray-200 shadow-xl rounded-md pb-1 !w-[300px] space-y-2", openUpward ? "bottom-full mb-1" : "top-full mt-1", alignRight ? "right-0" : "left-0"].join(" ")}>
                    {
                        (calendar && !isTimeOnly) &&
                        <>
                            <DayPicker
                                month={month}
                                onMonthChange={setMonth}
                                locale={ptBR}
                                mode="single"
                                navLayout="around"
                                showOutsideDays
                                selected={dayPickerValue}
                                onSelect={handleDayPickerSelect}
                                captionLayout="dropdown"
                                disabled={restrictions}
                                endMonth={new Date((new Date().getFullYear()) + 100, 11)}
                            />
                            {
                                ["LL", "LLL"].includes(dateFormat_) &&
                                <span title={"Selecionar horário"} className={"m-1 p-1 rounded-sm flex justify-center cursor-pointer hover:text-blue-600 hover:bg-[#e9ecef] focus:outline-none focus:text-blue-600"} onClick={() => {
                                    setTimeSelection(null);
                                    setCalendar(false);
                                }}>
                                    <FaClock size={16}/>
                                </span>
                            }
                        </>
                    }

                    {
                        (!calendar || isTimeOnly) &&
                        <>
                             {
                                timeSelection ?
                                    <div className="p-3">
                                        <div className="mb-1 text-center text-sm font-medium text-gray-600">
                                            {timeSelection.label}
                                        </div>
                                        <div className="grid grid-cols-4 gap-1">
                                            {timeSelection.options.map((option) => (
                                                <button
                                                    key={option}
                                                    type="button"
                                                    className={[
                                                        "rounded-[20px] px-3 py-2 text-sm transition-colors cursor-pointer text-gray-600 hover:bg-[#e9ecef] hover:text-blue-600",
                                                    ].join(" ")}
                                                    onClick={() => handleTimeSelection(option)}
                                                >
                                                    {String(option).padStart(2, "0")}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                    :
                                    <div className="flex items-center justify-center gap-1">
                                        <TimeColumn
                                            value={time.h}
                                            max={23}
                                            onChangeValue={(amount, max) => handleTimeChange("h", amount, max)}
                                            onOpenSelection={() => openTimeSelection("h")}
                                            selectionLabel="Selecionar hora"
                                        />

                                        <span className="text-gray-400 font-bold -mt-1">:</span>

                                        <TimeColumn
                                            value={time.m}
                                            max={59}
                                            onChangeValue={(amount, max) => handleTimeChange("m", amount, max)}
                                            onOpenSelection={() => openTimeSelection("m")}
                                            selectionLabel="Selecionar minuto"
                                        />

                                        {
                                            ["LTS", "LLL"].includes(dateFormat_) &&
                                            <>
                                                <span className="text-gray-400 font-bold -mt-1">:</span>
                                                <TimeColumn
                                                    value={time.s}
                                                    max={59}
                                                    onChangeValue={(amount, max) => handleTimeChange("s", amount, max)}
                                                    onOpenSelection={() => openTimeSelection("s")}
                                                    selectionLabel="Selecionar segundo"
                                                />
                                            </>
                                        }
                                    </div>
                            }
                            {
                                !isTimeOnly &&
                                <span title={"Selecionar data"} className={"m-1 p-1 rounded-sm hover:bg-[#e9ecef] flex justify-center cursor-pointer hover:text-blue-600 focus:outline-none focus:text-blue-600"} onClick={() => {
                                    setTimeSelection(null);
                                    setCalendar(true);
                                }}>
                                    <FaCalendar size={16}/>
                                </span>
                            }
                        </>
                    }

                </div>
            }
        </div>
    );
};

export default InputDatetime;